import React, { Component } from 'react';
import { Legend, Tooltip } from 'recharts';
import { Unit } from '@philips-internal/oswald-ui-constants';
import { bool, element, eventListener, number, oneOf, predefinedSize, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, enumValues, isEnumValid, isFunction } from '@philips-internal/oswald-ui-utils';
import { ChartAlignment, ChartClickType, ChartDynamicRenderType, ChartLayout, ChartLegendType, ChartType } from './base-chart-constants.js';
import { fitPredefinedSizeValue, getDynamicContent, validateChartSizes } from './base-chart-utils.js';
const marginShape = { bottom: number, left: number, right: number, top: number };
class BaseChart extends Component {
    constructor() {
        super(...arguments);
        this.getChartSizes = (rawHeight, rawWidth) => {
            const fittedHeight = fitPredefinedSizeValue(rawHeight);
            const fittedWidth = fitPredefinedSizeValue(rawWidth);
            const validSizes = validateChartSizes(fittedHeight, fittedWidth);
            if (!validSizes) {
                return [];
            }
            return [fittedHeight, fittedWidth];
        };
        this.onClick = (item) => {
            const { onClick } = this.props;
            if (isFunction(onClick)) {
                onClick(new ClickEvent({ value: item }));
            }
        };
        this.onDynamicContentRender = (event) => {
            const { onDynamicContentRender } = this.props;
            if (isFunction(onDynamicContentRender)) {
                onDynamicContentRender(event);
            }
        };
        this.onLegendClick = (item) => {
            const { hiddenLegends } = this.state;
            const updatedHiddenLegends = [...hiddenLegends];
            const key = item?.dataKey;
            if (key) {
                const includes = hiddenLegends.includes(key);
                if (includes) {
                    const itemIndex = updatedHiddenLegends.indexOf(key);
                    updatedHiddenLegends.splice(itemIndex, 1);
                }
                else {
                    updatedHiddenLegends.push(key);
                }
            }
            this.setState({ hiddenLegends: updatedHiddenLegends });
            this.onClick({ item, type: ChartClickType.LEGEND });
        };
        this.getTooltipDynamicContent = (data) => {
            const chartProps = { ...data, content: undefined };
            const dynamicContent = getDynamicContent(chartProps, '', ChartDynamicRenderType.TOOLTIP, this.onDynamicContentRender);
            if (!dynamicContent) {
                return (React.createElement(Tooltip, { ...chartProps }));
            }
            return dynamicContent;
        };
    }
    getClassName(type) {
        const { className } = this.props;
        return classNames({
            'c-chart': true,
            [`c-chart--${type}`]: isEnumValid(ChartType, type),
            [`${className}`]: Boolean(className),
        });
    }
    renderTooltip() {
        const { showTooltip, tooltip } = this.props;
        if (!showTooltip) {
            return null;
        }
        return (React.createElement(Tooltip, { ...tooltip, content: this.getTooltipDynamicContent }));
    }
    renderLegends() {
        const { legends, legendsMargin, showLegends } = this.props;
        if (!showLegends) {
            return null;
        }
        return (
        //@ts-ignore: Ignoring because recharts legend types are inconsistent
        (React.createElement(Legend, { ...legends, margin: legendsMargin, onClick: (event) => this.onLegendClick(event) })));
    }
    render() {
        return null;
    }
}
BaseChart.propTypes = {
    children: element.notEnumerable(),
    className: string,
    height: predefinedSize([Unit.PX, Unit.PERCENT]),
    legends: shape({
        align: oneOf([ChartAlignment.CENTER, ChartAlignment.LEFT, ChartAlignment.RIGHT]),
        height: number,
        iconSize: number,
        iconType: oneOf(enumValues(ChartLegendType)),
        layout: oneOf(enumValues(ChartLayout)),
        verticalAlign: oneOf([ChartAlignment.BOTTOM, ChartAlignment.LEFT, ChartAlignment.RIGHT]),
        width: number,
    }).group('legends'),
    legendsMargin: shape(marginShape).group('legends'),
    margin: shape(marginShape),
    minHeight: number,
    minWidth: number,
    onClick: eventListener,
    onDynamicContentRender: eventListener,
    showLegends: bool.group('legends'),
    showTooltip: bool.group('tooltip'),
    tooltip: shape({
        filterNull: bool,
        offset: number,
        separator: string,
    }).group('tooltip'),
    width: predefinedSize([Unit.PX, Unit.PERCENT]),
};
BaseChart.defaultProps = {
    data: [],
    height: '350px',
    minHeight: 0,
    minWidth: 0,
    showLegends: true,
    showTooltip: true,
    width: '100%',
};
BaseChart.defaultState = {
    hiddenLegends: [],
};
export { BaseChart, };
//# sourceMappingURL=base-chart.js.map